import React from "react";
import BasePage from "../BasePage";
import BGImage from "../../assets/img/Designer.webp";
import imgR from "../../assets/img/img-hero-r.webp";
import imgL from "../../assets/img/img-hero-l.webp";

const TermsOfService: React.FC = () => {
  return (
    <BasePage
      title="Terms of Service - UC Waves"
      description="Read the Terms of Service for UC Waves, including user obligations, platform usage, intellectual property, and liability limitations."
      keywords="Terms of Service, UC Waves, Rules, Obligations, Legal"
    >
      <main
        className="relative flex flex-col items-center justify-center pt-28 bg-cover bg-center min-h-screen px-6"
        style={{ backgroundImage: `url(${BGImage})` }}
      >
        {/* Header with images */}

        <div className="flex items-center gap-8 justify-between w-full max-w-5xl mb-10">
          <img
            src={imgL}
            alt="Hero decorative left"
            width={256} // largeur "md:w-40" en px
            height={256} // même valeur pour un carré
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
          <h1 className="text-7xl font-bold text-white text-center drop-shadow-lg">
            Terms of Service
          </h1>

          <img
            src={imgR}
            alt="Hero decorative right"
            width={256}
            height={256}
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
        </div>

        {/* Content container */}
        <div className="bg-white bg-opacity-90 backdrop-blur-md  p-8 max-w-5xl text-gray-800 drop-shadow-lg mb-16">
          <p className="text-sm text-gray-600 mb-8">
            Last updated: <span className="font-semibold">06.12.2025</span>
          </p>
          <p className="mb-6">
            Welcome to UcWaves. These Terms of Service (“Terms”) govern your
            access to and use of the UcWaves website located at{" "}
            <a
              href="https://www.ucwaves.com"
              className="text-primary-dark hover:underline"
            >
              https://www.ucwaves.com
            </a>{" "}
            (the “Website”) and the UcWaves decentralized application (the
            “dApp”), including all associated smart contracts, services, and
            functionalities made available by or through UcWaves.
          </p>
          <p className="mb-6">
            Please read these Terms carefully before using the Website or dApp.
            By accessing or using UcWaves, you agree to be legally bound by
            these Terms. If you do not agree with any part of these Terms, you
            must immediately discontinue use of the Website and dApp.
          </p>

          <ol className="list-decimal list-inside space-y-6">
            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Acceptance of Terms
              </h2>
              <p className="mb-2">
                By accessing, browsing, or using the UcWaves Website or dApp,
                you acknowledge that:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  You have read, understood, and agree to be bound by these
                  Terms.
                </li>
                <li>
                  You have the legal authority to enter into these Terms, either
                  on your own behalf or on behalf of an organization.
                </li>
                <li>
                  You are at least 18 years of age or the age of legal majority
                  in your jurisdiction.
                </li>
                <li>
                  Your access and use of UcWaves complies with all applicable
                  laws and regulations.
                </li>
                <li>
                  These Terms apply to all visitors, users, developers, and
                  participants interacting with UcWaves.
                </li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Changes to Terms</h2>
              <p>
                We reserve the right to modify, amend, or replace these Terms at
                any time without prior notice. Changes will be effective upon
                posting on the Website. It is your responsibility to check for
                updates periodically. Continued use of the Website or dApp after
                changes are made constitutes acceptance of the new Terms.
              </p>
              <p className="mt-2">
                If you do not agree with any amended Terms, your sole remedy is
                to stop using the Website and dApp.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Description of the Platform
              </h2>
              <p className="mb-2">
                UcWaves is a decentralized, non-custodial platform that allows
                users to interact with a token-based opinion-sharing and staking
                ecosystem. The core components include:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  <span className="font-semibold">NAU:</span> A base utility
                  token used for participation.
                </li>
                <li>
                  <span className="font-semibold">NAUY / NAUN:</span> Opinion
                  tokens minted by burning NAU to express a binary stance.
                </li>
                <li>
                  <span className="font-semibold">Staking mechanisms:</span>{" "}
                  Enabling users to earn rewards by staking NAUY or NAUN.
                </li>
                <li>
                  <span className="font-semibold">Smart contracts:</span>{" "}
                  Deployed on a public blockchain (e.g., Ethereum Layer 2 –
                  Base) and responsible for managing token interactions,
                  governance, and rewards.
                </li>
                <li>
                  <span className="font-semibold">Wallet connectivity:</span>{" "}
                  Users connect via non-custodial wallets (e.g., MetaMask,
                  Coinbase Wallet, WalletConnect) and interact directly with the
                  smart contracts.
                </li>
              </ul>
              <p className="mt-4">
                We do not offer investment services, provide financial advice,
                or act as intermediaries in token transactions. UcWaves is a
                protocol and interface for user-directed engagement with
                decentralized systems.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Eligibility and Compliance
              </h2>
              <p className="mb-2">
                To use UcWaves, you represent and warrant that:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  You are not a resident of a country subject to international
                  sanctions or legal restrictions related to crypto assets.
                </li>
                <li>
                  You are not listed on any government or international
                  sanctions list.
                </li>
                <li>
                  You are not using the platform for illegal, fraudulent, or
                  prohibited purposes.
                </li>
                <li>
                  You are responsible for ensuring that your use of the platform
                  complies with the laws and regulations of your jurisdiction.
                </li>
                <li>
                  You are solely responsible for determining whether your use of
                  UcWaves is lawful and permissible under applicable laws.
                  UcWaves disclaims all liability for users acting in violation
                  of such laws.
                </li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Non-Custodial System
              </h2>
              <p className="mb-2">UcWaves is fully non-custodial. We do not:</p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Store private keys</li>
                <li>Access or hold user funds</li>
                <li>Control or reverse any blockchain transactions</li>
              </ul>
              <p className="mt-2">
                You are solely responsible for securing your wallet, seed
                phrase, and private keys. Any loss of access to your wallet will
                result in irretrievable loss of access to your funds and tokens.
                We cannot recover your assets under any circumstance.
              </p>
              <p className="mt-2">
                By using UcWaves, you accept full responsibility for your
                digital assets and wallet security.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Platform Usage and Rules of Conduct
              </h2>
              <p className="mb-2">
                You agree to use UcWaves only for lawful purposes and in
                accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Violate any law, regulation, or third-party rights</li>
                <li>
                  Exploit or attack smart contracts or the Website
                  infrastructure
                </li>
                <li>
                  Use the platform to promote or engage in illegal activity
                </li>
                <li>
                  Attempt to gain unauthorized access to the Website or dApp
                </li>
                <li>
                  Use bots or scripts to interact with the smart contracts in a
                  manipulative way
                </li>
                <li>
                  Attempt to front-run or manipulate staking or token prices
                </li>
                <li>
                  Upload or transmit viruses, malware, or any other harmful code
                </li>
              </ul>
              <p className="mt-2">
                We reserve the right, without obligation, to monitor your use of
                the platform and take action to preserve system integrity and
                fairness. Violations of these rules may result in restriction of
                access and reporting to regulatory authorities.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Intellectual Property
              </h2>
              <p>
                All content made available on the Website, including logos,
                branding, software, documentation, and user interface elements,
                is the property of UcWaves or its contributors and is protected
                by intellectual property laws. You may not:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  Copy, modify, or distribute content without explicit
                  permission
                </li>
                <li>
                  Use the brand or logo in a misleading or unauthorized manner
                </li>
                <li>
                  Claim any part of the codebase or documentation as your own
                  work
                </li>
              </ul>
              <p className="mt-2">
                Open-source components are subject to their respective licenses.
                Any forks or adaptations must comply with these licenses.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Token Interaction Disclaimer
              </h2>
              <p>
                Interacting with NAU, NAUY, and NAUN tokens through the platform
                involves the use of public blockchain infrastructure. We do not
                control token issuance, pricing, or on-chain behavior. You
                acknowledge that:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Token prices are not guaranteed</li>
                <li>Token availability and staking rewards are not assured</li>
                <li>Transactions are final and irreversible</li>
                <li>
                  UcWaves provides no warranty or refund mechanism for user
                  errors
                </li>
              </ul>
              <p className="mt-2">
                You are solely responsible for understanding token mechanics,
                smart contract risks, and market behavior before participating.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Smart Contract Risk Assumption
              </h2>
              <p>
                While every effort is made to ensure the security of the smart
                contracts deployed by UcWaves, smart contracts inherently carry
                risks such as:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Logic bugs</li>
                <li>Reentrancy attacks</li>
                <li>Flash loan exploits</li>
                <li>Oracle manipulation</li>
              </ul>
              <p className="mt-2">
                By interacting with the protocol, you assume all risk associated
                with the smart contracts. UcWaves makes no guarantees regarding
                the correctness, safety, or uptime of the deployed contracts.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                No Guarantee of Availability
              </h2>
              <p>
                UcWaves does not guarantee that the Website or dApp will always
                be available, uninterrupted, or error-free. We may:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Temporarily disable access for maintenance or upgrades</li>
                <li>
                  Limit access in specific regions to comply with legal
                  requirements
                </li>
                <li>
                  Modify, suspend, or discontinue parts of the platform without
                  prior notice
                </li>
              </ul>
              <p className="mt-2">
                We are not liable for any loss resulting from platform downtime
                or unavailability.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Governance and Upgrades
              </h2>
              <p>
                UcWaves may, in the future, be governed by a decentralized
                auNAUNomous organization (DAO). Until that time, decisions
                regarding:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Smart contract upgrades</li>
                <li>Parameter adjustments</li>
                <li>Reward structures</li>
                <li>Technical fixes</li>
              </ul>
              <p className="mt-2">
                No promise is made regarding the timeline or structure of
                governance transitions.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Community and Open Source Contributions
              </h2>
              <p>
                UcWaves encourages community involvement. Contributions to
                open-source repositories must:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Comply with the applicable license terms</li>
                <li>Not introduce malicious or unsafe code</li>
                <li>Include clear and auditable commit messages</li>
                <li>Respect the architecture and integrity of the protocol</li>
              </ul>
              <p className="mt-2">
                We reserve the right to reject contributions or requests that do
                not meet quality or security standards.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                External Links and Services
              </h2>
              <p>
                The UcWaves platform may provide links to third-party services
                including wallets, block explorers, or exchanges. These are
                provided for convenience only. We do not:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Endorse third-party services</li>
                <li>
                  Guarantee the accuracy, security, or legality of third-party
                  content
                </li>
                <li>
                  Accept liability for losses caused by third-party interactions
                </li>
              </ul>
              <p className="mt-2">
                You use third-party services at your own risk.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Taxes and Reporting
              </h2>
              <p>You are solely responsible for:</p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  Determining your tax obligations related to digital assets
                </li>
                <li>
                  Reporting gains, losses, and income in accordance with
                  applicable laws
                </li>
                <li>
                  Paying any taxes or fees associated with your activities
                </li>
              </ul>
              <p className="mt-2">
                UcWaves does not provide tax documentation or financial
                statements. Consult a tax professional for advice on your
                obligations.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless UcWaves, its
                developers, affiliates, and contributors from any claim, demand,
                liability, damage, loss, or expense arising out of or related
                to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Your use or misuse of the platform</li>
                <li>Violation of these Terms</li>
                <li>Violation of applicable laws or third-party rights</li>
                <li>Disputes with other users</li>
              </ul>
              <p className="mt-2">
                This indemnification obligation will survive the termination of
                your use of the platform.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Disclaimers</h2>
              <p>
                The UcWaves platform is provided on an "as-is" and
                "as-available" basis. To the fullest extent permitted by law, we
                disclaim all warranties, express or implied, including but not
                limited to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Warranties of merchantability</li>
                <li>Warranties of fitness for a particular purpose</li>
                <li>Warranties of non-infringement</li>
                <li>Warranties of uninterrupted availability</li>
              </ul>
              <p className="mt-2">
                We do not guarantee that the platform will meet your
                expectations or that it will be free of errors or
                vulnerabilities.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Limitation of Liability
              </h2>
              <p>
                To the maximum extent permitted by law, UcWaves and its
                contributors will not be liable for any:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Direct, indirect, incidental, or consequential damages</li>
                <li>
                  Loss of profits, data, goodwill, or other intangible losses
                </li>
                <li>
                  Damages arising from or related to use or inability to use the
                  platform
                </li>
                <li>Errors in smart contracts or blockchain interactions</li>
                <li>Security breaches or unauthorized access</li>
              </ul>
              <p className="mt-2">
                This limitation applies regardless of legal theory or whether we
                were advised of the possibility of such damages.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Termination and Suspension
              </h2>
              <p>
                We reserve the right to suspend or terminate your access to
                UcWaves at any time, with or without cause, and without
                liability. Grounds for termination include:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Breach of these Terms</li>
                <li>Violation of law</li>
                <li>Suspicious or malicious behavior</li>
                <li>Regulatory order or notice</li>
              </ul>
              <p className="mt-2">
                Upon termination, your right to use the platform will
                immediately cease. You may still retain access to your tokens
                through your wallet.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Governing Law</h2>
              <p>
                These Terms shall be governed and construed in accordance with
                the laws of the jurisdiction in which UcWaves operates or is
                registered, without regard to its conflict of laws principles.
              </p>
              <p className="mt-2">
                Any dispute, controversy, or claim arising from or relating to
                these Terms or the use of UcWaves will be resolved through
                binding arbitration or courts of competent jurisdiction in such
                jurisdiction, unless otherwise required by law.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Severability and Waiver
              </h2>
              <p>
                If any provision of these Terms is found to be invalid or
                unenforceable under applicable law, the remaining provisions
                will continue in full force and effect. Our failure to enforce
                any right or provision shall not be deemed a waiver of that
                right.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Entire Agreement</h2>
              <p>
                These Terms constitute the entire agreement between you and
                UcWaves with respect to the platform and supersede any prior or
                contemporaneous communications, whether oral or written.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Contact</h2>
              <p className="mb-2">
                For any questions regarding these Terms, please contact:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  Email:{" "}
                  <span className="font-semibold">contact@ucwaves.com</span>
                </li>
                <li>
                  Website:{" "}
                  <a
                    href="https://www.ucwaves.com"
                    className="text-primary-dark hover:underline"
                  >
                    https://www.ucwaves.com
                  </a>
                </li>
              </ul>
            </li>
          </ol>
        </div>
      </main>
    </BasePage>
  );
};

export default TermsOfService;
